import { AxiosResponse, HttpStatusCode } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import calculatePrice from '../../../utils/calculators/calculatePrice';
import {
  ParentConsumerContextInterface,
  ParentConsumerUpdateInterface
} from '../../../shared/type/parentConsumerContext.type';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import { useAlertContext } from '../../../context/AlertProvider';
import { useApi } from '../../../context/ApiProvider';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import { ConsumerDto } from '../../../shared/type/parentDashboardContext.type';

export const useParentAssignedAccounts = () => {

  function isGreater(value1: string, value2: string): boolean {
    const parse = (val: string): number => parseFloat(val.replace(',', '.'));

    const num1 = parse(value1);
    const num2 = parse(value2);
    return num1 > num2;
  }

  const { t } = useSafeTranslation();
  const { apiParentConsumerController } = useApi();
  const { setShow, setAlertProperties } = useAlertContext();
  const { parentDashboardContext, fetchParentDashboardContext } =
    useParentDashboardContextContext();
  const [customers, setCustomers] = useState<ConsumerDto[] | null>(null);
  const [consumerId, setConsumerId] = useState<number | null>(null);
  const [consumerUpdateContext, setConsumerUpdateContext] = useState<
    ParentConsumerUpdateInterface | undefined
  >(undefined);
  const [consumerContext, setConsumerContext] = useState<
    ParentConsumerContextInterface | undefined
  >(undefined);
  const [areButtonActive, setAreButtonsActive] = useState<boolean>(false);
  const { setBasketStatus } = useParentDashboardContextContext();

  const findTenantIdByConsumerId = (consumerId: number): number | null => {
    const tenant = parentDashboardContext?.tenants.find((tenant) =>
      tenant.consumers.some((consumer) => consumer.consumerDto.id === consumerId)
    );
    return tenant ? tenant.tenantId : null;
  };

  const fetchUpdateConsumerContext = async (id: number) => {
    try {
      const tenantId = findTenantIdByConsumerId(id) || 0;
      const response: AxiosResponse<ParentConsumerContextInterface> =
        await apiParentConsumerController('payment-context').get(`${tenantId}/${id}`);
      setConsumerUpdateContext(response.data.consumerUpdateDto);
      setConsumerContext(response.data);
      setCreditLimitError(isGreater(
        response.data.consumerUpdateDto.creditLimitMoneyString ?? "0,00",
        calculatePrice(response.data.maximalCreditMoney, null)
      ));
    } catch (error) {
      console.error('Failed to fetch consumer context:', error);
    }
  };

  const updateConsumer = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    let allowedToBuy, creditLimitMoneyString, remarks, cardNumber;
    if (consumerUpdateContext) {
      allowedToBuy = consumerUpdateContext.allowedToBuy;
      creditLimitMoneyString = consumerUpdateContext.creditLimitMoneyString;
      remarks = consumerUpdateContext.remarks;
      cardNumber = consumerUpdateContext.cardNumber;
    }

    const tenantId = findTenantIdByConsumerId(Number(consumerId));

    try {
      const response = await apiParentConsumerController('update').put(`/${tenantId}`, {
        consumerId,
        allowedToBuy,
        cardNumber,
        creditLimitMoneyString,
        remarks
      });

      if (response.status) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_assigned_accounts.success'),
          description: t('parent_assigned_accounts.update_success'),
          status: 'success'
        });
        fetchParentDashboardContext();
        setShow(true);
      }
    } catch (error: any) {
      if (error.response.status === HttpStatusCode.Conflict) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_assigned_accounts.conflict'),
          description: `${t('parent_assigned_accounts.error')}: ${error.response.data.errorMessage}`,
          status: 'warning'
        });
      } else {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_assigned_accounts.error'),
          description: `${t('parent_assigned_accounts.update_error')} - ${error.response.data.violations[0].fieldName}: ${error.response.data.violations[0].message}`,
          status: 'error'
        });
      }
      setShow(true);
    }
  };

  const getAllConsumers = () => {
    if (!parentDashboardContext || !parentDashboardContext.tenants) {
      return [];
    }

    return parentDashboardContext.tenants.flatMap((tenant) => {
      if (tenant.consumers && Array.isArray(tenant.consumers)) {
        return tenant.consumers.map((consumer) => consumer.consumerDto);
      }
      return [];
    });
  };

  useEffect(() => {
    if (
      consumerUpdateContext?.creditLimitMoneyString === null ||
      consumerUpdateContext?.creditLimitMoneyString === ''
    ) {
      setConsumerUpdateContext((prev) => {
        if (!prev) return undefined;

        return {
          ...prev,
          creditLimitMoneyString: '0,00'
        };
      });
    }
  }, [consumerUpdateContext?.creditLimitMoneyString]);

  useEffect(() => {
    setBasketStatus(() => ({ show: false, forceShow: false }));
  }, []);

  useEffect(() => {
    const consumers = getAllConsumers();
    setCustomers(consumers);

    if (consumers.length > 0) {
      const defaultConsumerId = consumers[0].id;
      setConsumerId(defaultConsumerId);
    }
  }, [parentDashboardContext]);

  useEffect(() => {
    if (consumerId !== null) {
      fetchUpdateConsumerContext(consumerId);
    }
  }, [consumerId]);

  const selectedConsumer = customers?.find((consumer) => consumer.id === consumerId);
  const [creditLimitError, setCreditLimitError] = useState<boolean>(false);
  return {
    selectedConsumer,
    updateConsumer,
    areButtonActive,
    setAreButtonsActive,
    customers,
    setConsumerId,
    consumerId,
    setConsumerUpdateContext,
    consumerUpdateContext,
    setConsumerContext,
    consumerContext,
    setCustomers,
    creditLimitError,
    setCreditLimitError,
    fetchUpdateConsumerContext
  };
};
